'use client';

import React, { useState, useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ProjectImage } from '../../components/ui/project-image';
import { cn } from '../../lib/utils';
import { Icon } from '@iconify/react';

/**
 * ProjectGallery component
 * Displays a grid of project images with smooth animations
 * Features a full-screen lightbox view for better image viewing
 * Used in the project detail page to showcase additional images
 */
interface ProjectGalleryProps {
  images: { _key: string; url: string }[];
  projectTitle: string;
  className?: string;
}

export function ProjectGallery({ images, projectTitle, className }: ProjectGalleryProps) {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  // Skip rendering if no images
  if (!images || images.length === 0) return null;

  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (listRef.current) {
      gsap.fromTo(listRef.current.children,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.5, stagger: 0.1, ease: "power2.out" }
      );
    }
  }, [images]);

  // Handle lightbox navigation
  const nextImage = () => {
    if (selectedImage === null) return;
    setSelectedImage((selectedImage + 1) % images.length);
  };

  const prevImage = () => {
    if (selectedImage === null) return;
    setSelectedImage((selectedImage - 1 + images.length) % images.length);
  };

  const closeLightbox = () => setSelectedImage(null);

  return (
    <>
      <section className={cn(
        "mb-12 bg-white dark:bg-slate-800/30 p-8 rounded-xl border border-slate-200 dark:border-slate-700/50 shadow-md",
        className
      )}>
        <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-8">Gallery</h2>

        <div
          ref={listRef}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {images.map((image, index) => (
            <div
              key={index}
              className="group cursor-pointer"
              onClick={() => setSelectedImage(index)}
            >
              <div className="aspect-[16/9] rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700/50 shadow-sm hover:shadow-lg transition-all duration-300 group-hover:scale-[1.02]">
                <ProjectImage
                  src={image.url}
                  alt={`${projectTitle} - Gallery image ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 text-center">
                Click to enlarge
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage !== null && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center animate-in fade-in duration-300"
          onClick={closeLightbox}
        >
          <div className="relative w-full h-full flex items-center justify-center px-4 sm:px-10">
            {/* Close button */}
            <button
              className="absolute top-4 right-4 text-white p-2 rounded-full bg-black/20 hover:bg-black/40 transition-colors z-10"
              onClick={closeLightbox}
            >
              <Icon icon="lucide:x" className="w-6 h-6" />
            </button>

            {/* Navigation buttons */}
            <button
              className="absolute left-4 sm:left-10 text-white p-3 rounded-full bg-black/20 hover:bg-black/40 transition-colors z-10"
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
            >
              <Icon icon="lucide:chevron-left" className="w-6 h-6" />
            </button>
            <button
              className="absolute right-4 sm:right-10 text-white p-3 rounded-full bg-black/20 hover:bg-black/40 transition-colors z-10"
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
            >
              <Icon icon="lucide:chevron-right" className="w-6 h-6" />
            </button>

            {/* Image */}
            <div
              className="w-full max-w-5xl h-full flex items-center justify-center p-4 sm:p-8 animate-in zoom-in duration-300"
            >
              <div className="max-h-full relative w-full max-w-5xl">
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <div className="absolute inset-0 rounded-lg overflow-hidden">
                    <ProjectImage
                      src={images[selectedImage].url}
                      alt={`${projectTitle} - Gallery image ${selectedImage + 1}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Image counter */}
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-white bg-black/40 px-4 py-2 rounded-full text-sm font-medium">
              {selectedImage + 1} / {images.length}
            </div>
          </div>
        </div>
      )}
    </>
  );
} 